import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import {
  GraduationCap,
  FileText,
  ClipboardCheck,
  Clock,
  Users,
  Shield,
  ChevronRight,
  Star,
  ArrowRight,
  BookOpen,
  Award,
  ScrollText,
  BadgeCheck,
  Sparkles,
  MonitorSmartphone,
  Bell,
  Zap,
  CheckCircle2,
  Menu,
  X,
  ChevronDown,
} from 'lucide-react';
import { LandingPageChatbot } from './LandingPageChatbot';
import { supabase } from '../utils/supabase/client';
import { projectId, publicAnonKey } from '../utils/supabase/info';

interface LandingPageProps {
  onGetStarted: () => void;
}

interface QueueRecord {
  id: string;
  studentName?: string;
  serviceName?: string;
  windowNumber?: number;
  queueNumber?: number;
  status?: string;
  joinedAt?: string;
  servedAt?: string;
  completedAt?: string;
  onHoldAt?: string;
  totalHoldDuration?: number;
  feedbackRating?: number;
  feedbackComment?: string;
  feedbackAt?: string;
}

interface Testimonial {
  name: string;
  role: string;
  quote: string;
  rating: number;
}

/* ------------------------------------------------------------------ */
/*  Reusable animation wrapper — fades/slides children in on scroll   */
/* ------------------------------------------------------------------ */
function FadeInSection({
  children,
  className = '',
  delay = 0,
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => setVisible(true), delay);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [delay]);

  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      } ${className}`}
    >
      {children}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Data                                                              */
/* ------------------------------------------------------------------ */

const services = [
  {
    icon: BookOpen,
    title: 'Enrollment',
    description:
      'Seamless enrollment processing for new and continuing students across all programs.',
    color: 'from-blue-500 to-blue-600',
    bg: 'bg-blue-50',
    iconColor: 'text-blue-600',
  },
  {
    icon: ScrollText,
    title: 'Transcript of Records',
    description:
      'Request official transcripts with end-to-end tracking from submission to release.',
    color: 'from-emerald-500 to-emerald-600',
    bg: 'bg-emerald-50',
    iconColor: 'text-emerald-600',
  },
  {
    icon: Award,
    title: 'Diploma Processing',
    description:
      `Track your diploma application status and get notified when it's ready for pickup.`,
    color: 'from-amber-500 to-amber-600',
    bg: 'bg-amber-50',
    iconColor: 'text-amber-600',
  },
  {
    icon: BadgeCheck,
    title: 'Certification & CAV',
    description:
      'Certification, Authentication & Verification requests processed digitally.',
    color: 'from-violet-500 to-violet-600',
    bg: 'bg-violet-50',
    iconColor: 'text-violet-600',
  },
  {
    icon: ClipboardCheck,
    title: 'Clearance',
    description:
      'Complete your clearance requirements online — upload documents and track approvals.',
    color: 'from-rose-500 to-rose-600',
    bg: 'bg-rose-50',
    iconColor: 'text-rose-600',
  },
  {
    icon: FileText,
    title: 'Good Moral & Other Docs',
    description:
      'Request Good Moral Certificates, Honorable Dismissal, grade inquiries, and more.',
    color: 'from-cyan-500 to-cyan-600',
    bg: 'bg-cyan-50',
    iconColor: 'text-cyan-600',
  },
];

const features = [
  {
    icon: Zap,
    title: 'Real-Time Queue',
    description: `See your position in the queue live — no more guessing when it's your turn.`,
  },
  {
    icon: MonitorSmartphone,
    title: 'Queue Monitor Display',
    description: 'Large-screen monitor view for lobby displays shows current serving numbers.',
  },
  {
    icon: Bell,
    title: 'Smart Notifications',
    description: 'Get notified when your number is approaching so you never miss your turn.',
  },
  {
    icon: Sparkles,
    title: 'AI Assistant',
    description: 'Our AI-powered assistant answers FAQs and guides you through every step.',
  },
  {
    icon: Shield,
    title: 'Secure & Private',
    description: 'Your academic records and personal data are protected with enterprise-grade security.',
  },
  {
    icon: Clock,
    title: 'Estimated Wait Times',
    description: `Know how long you'll wait before you even arrive at the registrar window.`,
  },
];

// Initial/fallback stats - will be replaced by realtime data
const initialStats = [
  { value: '0', label: 'Queues Served' },
  { value: '6', label: 'Service Windows' },
  { value: '—', label: 'Avg. Wait Time' },
  { value: '100%', label: 'Completion Rate' },
];

const initialTestimonials: Testimonial[] = [
  {
    name: 'Maria Santos',
    role: 'BS Computer Science - DCS',
    quote:
      "I used to spend hours in line. Now I just join the queue from my dorm and walk over when it's almost my turn.",
    rating: 5,
  },
  {
    name: 'Carlos Reyes',
    role: 'MBA - Graduate School',
    quote:
      'Requesting my transcript was seamless — the document tracker kept me updated every step of the way.',
    rating: 5,
  },
  {
    name: 'Jenn Panganiban',
    role: 'Juris Doctor - COL',
    quote:
      'The system has drastically reduced lobby congestion. Processing times are faster and students are happier.',
    rating: 5,
  },
];

const steps = [
  {
    step: '01',
    title: 'Create Your Account',
    description: 'Sign up with your NEMSU email and student ID to get started in seconds.',
  },
  {
    step: '02',
    title: 'Select a Service',
    description: 'Choose the registrar service you need — enrollment, TOR, clearance, and more.',
  },
  {
    step: '03',
    title: 'Join the Queue',
    description: 'Get a virtual queue number and track your position from anywhere on campus.',
  },
  {
    step: '04',
    title: 'Get Served',
    description: `Receive a notification when it's your turn and head to the assigned window.`,
  },
];

const faqs = [
  {
    q: 'Do I need to be on campus to join the queue?',
    a: `No — you can join the queue remotely from your phone or laptop. You'll receive a notification when your turn is approaching so you can head to the registrar office.`,
  },
  {
    q: 'What documents do I need to create an account?',
    a: `You'll need your NEMSU student ID and your email address. The signup process takes less than a minute.`,
  },
  {
    q: 'How are queue positions assigned?',
    a: `Positions are assigned on a first-come, first-served basis. You'll see your real-time position and estimated wait time in your dashboard.`,
  },
  {
    q: 'Can I request multiple services at once?',
    a: 'Yes! You can queue for multiple services simultaneously. Each request will be tracked separately in your dashboard.',
  },
  {
    q: 'Is there a fee for using the queue system?',
    a: 'The queue system itself is completely free. Standard document processing fees set by the university still apply and can be paid via LBP bank transfer or at the cashier window.',
  },
];

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export function LandingPage({ onGetStarted }: LandingPageProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [scrolled, setScrolled] = useState(false);
  const [stats, setStats] = useState(initialStats);
  const [allTestimonials, setAllTestimonials] = useState<Testimonial[]>(initialTestimonials);
  const [testimonialPage, setTestimonialPage] = useState(0);
  const [queueData, setQueueData] = useState({
    userNumber: 'A-042',
    nowServing: 'A-039',
    estimatedWait: '~8 min',
    completedService: 'Transcript of Records',
    aiMessage: 'Your clearance is 80% complete — just upload your library clearance to finish!',
    waitProgress: 78,
  });
  const [loading, setLoading] = useState(true);

  const formatName = useCallback((name: string | undefined, index: number) => {
    if (!name) return `Student ${index + 1}`;
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0];
    const lastInitial = parts[parts.length - 1]?.charAt(0) || '';
    return `${parts[0]} ${lastInitial}.`;
  }, []);

  const buildTestimonials = useCallback(
    (queues: QueueRecord[]) => {
      const withFeedback = queues
        .filter((q) => q.feedbackComment || q.feedbackRating)
        .sort((a, b) => {
          const aTime = new Date(a.feedbackAt || a.completedAt || a.joinedAt || 0).getTime();
          const bTime = new Date(b.feedbackAt || b.completedAt || b.joinedAt || 0).getTime();
          return bTime - aTime;
        });

      const mapped = withFeedback.map((q, index) => ({
        name: formatName(q.studentName, index),
        role: q.serviceName ? `${q.serviceName} Request` : 'NEMSU Student',
        quote: q.feedbackComment || 'Great service experience.',
        rating: Math.max(1, Math.min(5, q.feedbackRating || 5)),
      }));

      if (mapped.length === 0) {
        return initialTestimonials;
      }

      return mapped;
    },
    [formatName]
  );

  const fetchRealtimeData = useCallback(async () => {
    const API_BASE = `https://${projectId}.supabase.co/functions/v1/make-server-d4ddf86c`;

    try {
      const [queuesRes, diagnosticsRes] = await Promise.all([
        fetch(`${API_BASE}/queues`, {
          headers: { Authorization: `Bearer ${publicAnonKey}` },
        }),
        fetch(`${API_BASE}/diagnostics`, {
          headers: { Authorization: `Bearer ${publicAnonKey}` },
        }),
      ]);

      if (!queuesRes.ok) throw new Error(`Failed to fetch queues (${queuesRes.status})`);
      if (!diagnosticsRes.ok) throw new Error(`Failed to fetch diagnostics (${diagnosticsRes.status})`);

      const queuesJson = await queuesRes.json();
      const diagnosticsJson = await diagnosticsRes.json();

      const queues = (queuesJson.queues || []) as QueueRecord[];
      const activeWindows =
        diagnosticsJson?.system?.activeWindows ??
        (diagnosticsJson?.windows || []).filter((w: { isActive?: boolean }) => w?.isActive).length ??
        0;

      const sortedQueues = [...queues].sort((a, b) =>
        new Date(a.joinedAt || 0).getTime() - new Date(b.joinedAt || 0).getTime()
      );

      const serving = sortedQueues.filter((q) => q.status === 'serving');
      const waiting = sortedQueues.filter((q) => q.status === 'waiting');
      const completed = sortedQueues.filter((q) => q.status === 'completed');

      // Average serving time: counts only active serving time (paused during on_hold)
      const completedWithTimes = completed.filter(
        (q) => q.servedAt && q.completedAt
      );

      let avgWait = '<10 min';
      if (completedWithTimes.length > 0) {
        const totalWaitTime = completedWithTimes.reduce((sum, q) => {
          const servingTime = new Date(q.completedAt || '').getTime() - new Date(q.servedAt || '').getTime() - (q.totalHoldDuration || 0);
          return sum + (servingTime > 0 ? servingTime : 0);
        }, 0);
        const avgMs = totalWaitTime / completedWithTimes.length;
        const avgMin = Math.round(avgMs / 60000);
        avgWait = avgMin > 0 ? `~${avgMin} min` : '<1 min';
      }

      const servingQueue = sortedQueues.find((q) => q.status === 'serving') || null;
      const waitingSample = sortedQueues.find((q) => q.status === 'waiting') || null;
      const completedSample = completed.length > 0 ? completed[completed.length - 1] : null;

      const totalQueuesCreated = queues.length;
      const completionRate = totalQueuesCreated > 0
        ? Math.round((completed.length / totalQueuesCreated) * 100)
        : 0;

      setStats([
        {
          value: totalQueuesCreated > 0 ? totalQueuesCreated.toLocaleString() : initialStats[0].value,
          label: 'Queues Served',
        },
        {
          value: activeWindows > 0 ? activeWindows.toString() : initialStats[1].value,
          label: 'Service Windows',
        },
        {
          value: avgWait,
          label: 'Avg. Wait Time',
        },
        {
          value: `${completionRate}%`,
          label: 'Completion Rate',
        },
      ]);

      if (waitingSample || servingQueue) {
        const userNum = waitingSample?.queueNumber || servingQueue?.queueNumber || 1;
        const nowServingNum = servingQueue?.queueNumber || userNum;
        const estimatedWaitTime = waiting.length > 0 ? `~${Math.max(waiting.length * 3, 5)} min` : '<2 min';
        const serviceName = completedSample?.serviceName || 'Transcript of Records';
        const completedProgress = completed.length > 0 ? Math.min(95, completed.length * 8) : 78;
        const windowNumber = servingQueue?.windowNumber || waitingSample?.windowNumber || 1;
        const windowPrefix = String.fromCharCode(64 + Math.max(1, windowNumber));

        setQueueData({
          userNumber: `${windowPrefix}-${String(userNum).padStart(3, '0')}`,
          nowServing: `${windowPrefix}-${String(nowServingNum).padStart(3, '0')}`,
          estimatedWait: estimatedWaitTime,
          completedService: serviceName,
          aiMessage: `You have ${waiting.length} people ahead in queue. Visit us when estimated time arrives!`,
          waitProgress: completedProgress,
        });
      }

      setAllTestimonials(buildTestimonials(queues));
      setLoading(false);
    } catch (error) {
      console.error('Error fetching realtime data:', error);
      setLoading(false);
    }
  }, [buildTestimonials]);

  useEffect(() => {
    fetchRealtimeData();

    const channel = supabase
      .channel('landing-kv')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'kv_store_d4ddf86c' },
        (payload) => {
          const key = (payload.new as { key?: string })?.key || (payload.old as { key?: string })?.key;
          if (key && (key.startsWith('queue:') || key.startsWith('window:'))) {
            fetchRealtimeData();
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchRealtimeData]);

  useEffect(() => {
    if (allTestimonials.length <= 3) return undefined;
    const interval = setInterval(() => {
      setTestimonialPage((prev) => (prev + 1) % allTestimonials.length);
    }, 60000);
    return () => clearInterval(interval);
  }, [allTestimonials.length]);

  const visibleTestimonials = useMemo(() => {
    if (!allTestimonials.length) return initialTestimonials.slice(0, 3);
    const take = Math.min(3, allTestimonials.length);
    const start = testimonialPage % allTestimonials.length;
    const items: Testimonial[] = [];
    for (let i = 0; i < take; i += 1) {
      const idx = (start + i) % allTestimonials.length;
      items.push(allTestimonials[idx]);
    }
    return items;
  }, [allTestimonials, testimonialPage]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-white text-foreground overflow-x-hidden">
      {/* ───────────── Inline keyframes ───────────── */}
      <style>{`
        @keyframes hero-float {
          0%,100%{transform:translateY(0)}
          50%{transform:translateY(-18px)}
        }
        @keyframes hero-pulse {
          0%,100%{opacity:.45;transform:scale(1)}
          50%{opacity:.7;transform:scale(1.15)}
        }
        @keyframes gradient-x {
          0%{background-position:0% 50%}
          50%{background-position:100% 50%}
          100%{background-position:0% 50%}
        }
        .animate-hero-float{animation:hero-float 6s ease-in-out infinite}
        .animate-hero-pulse{animation:hero-pulse 8s ease-in-out infinite}
        .animate-gradient-x{background-size:200% 200%;animation:gradient-x 8s ease infinite}
      `}</style>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/*  NAVIGATION                                                    */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-white/90 backdrop-blur-lg shadow-sm border-b border-border/50'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-4">
          {/* Logo */}
          <button onClick={() => scrollTo('hero')} className="flex items-center gap-3 group">
            <div className="relative">
              <img
                src="/regiqueueai.png"
                alt="NEMSU Registrar"
                className="h-10 w-10 rounded-xl shadow-md group-hover:scale-105 transition-transform"
              />
            </div>
            <div className="hidden sm:block text-left">
              <span className="text-lg font-bold bg-gradient-to-r from-blue-700 to-indigo-600 bg-clip-text text-transparent">
                Serve Smart
              </span>
              <span className="block text-[11px] leading-tight text-muted-foreground -mt-0.5">
                NEMSU Registrar Queue
              </span>
            </div>
          </button>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-1">
            {['Services', 'Features', 'How It Works', 'FAQ'].map((item) => (
              <button
                key={item}
                onClick={() => scrollTo(item.toLowerCase().replace(/\s+/g, '-'))}
                className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground rounded-lg hover:bg-accent/60 transition-colors"
              >
                {item}
              </button>
            ))}
            <Button
              onClick={onGetStarted}
              className="ml-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 transition-all"
            >
              Get Started
              <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
          </nav>

          {/* Mobile hamburger */}
          <button
            className="md:hidden p-2 rounded-lg hover:bg-accent/60"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white/95 backdrop-blur-xl border-b border-border/50 px-6 pb-4 space-y-1 animate-in slide-in-from-top-2">
            {['Services', 'Features', 'How It Works', 'FAQ'].map((item) => (
              <button
                key={item}
                onClick={() => scrollTo(item.toLowerCase().replace(/\s+/g, '-'))}
                className="block w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-accent/60 transition-colors"
              >
                {item}
              </button>
            ))}
            <Button
              onClick={onGetStarted}
              className="w-full mt-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white"
            >
              Get Started
              <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
          </div>
        )}
      </header>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/*  HERO                                                          */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <section
        id="hero"
        className="relative min-h-[100dvh] flex items-center justify-center overflow-hidden"
      >
        {/* Background blobs */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute -top-32 -left-32 w-[500px] h-[500px] rounded-full bg-blue-400/20 blur-[100px] animate-hero-pulse" />
          <div className="absolute top-1/3 -right-40 w-[420px] h-[420px] rounded-full bg-indigo-400/15 blur-[100px] animate-hero-pulse" style={{ animationDelay: '2s' }} />
          <div className="absolute -bottom-20 left-1/3 w-[350px] h-[350px] rounded-full bg-violet-400/15 blur-[100px] animate-hero-pulse" style={{ animationDelay: '4s' }} />
          {/* Dot pattern */}
          <div
            className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage:
                'radial-gradient(circle, #000 1px, transparent 1px)',
              backgroundSize: '24px 24px',
            }}
          />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 pt-28 pb-20 grid lg:grid-cols-2 gap-12 items-center">
          {/* Left — Text */}
          <div className="max-w-xl">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-50 border border-blue-200/60 text-blue-700 text-sm font-medium mb-6">
              <GraduationCap className="h-4 w-4" />
              NEMSU Registrar Office
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.1] tracking-tight">
              Your Registrar,{' '}
              <span className="bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 bg-clip-text text-transparent animate-gradient-x">
                Reimagined
              </span>
            </h1>

            <p className="mt-6 text-lg text-muted-foreground leading-relaxed max-w-lg">
              Skip the long lines. Join the queue from your phone, track your
              position in real time, and get notified the moment it's your turn
              — all from the <span className="font-semibold text-foreground">Serve Smart</span> platform.
            </p>

            <div className="mt-8 flex flex-wrap gap-4">
              <Button
                size="lg"
                onClick={onGetStarted}
                className="h-13 px-8 text-base bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-xl shadow-blue-500/25 hover:shadow-blue-500/40 hover:scale-[1.02] active:scale-[0.98] transition-all"
              >
                Get Started — It's Free
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button
                variant="outline"
                size="lg"
                onClick={() => scrollTo('services')}
                className="h-13 px-8 text-base border-2 hover:bg-accent/60 hover:scale-[1.02] active:scale-[0.98] transition-all"
              >
                Explore Services
              </Button>
            </div>

            {/* Mini stats */}
            <div className="mt-12 flex flex-wrap gap-8">
              {stats.map((s) => (
                <div key={s.label}>
                  <p className="text-2xl font-bold text-foreground">{loading ? '⋯' : s.value}</p>
                  <p className="text-sm text-muted-foreground">{s.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right — Illustration card stack */}
          <div className="hidden lg:flex justify-center items-center relative">
            <div className="relative w-[420px] h-[420px]">
              {/* Floating cards */}
              <div className="absolute top-0 right-0 w-64 bg-white rounded-2xl shadow-2xl shadow-blue-500/10 border border-border/50 p-5 animate-hero-float" style={{ animationDelay: '0s' }}>
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                    <Users className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold">Queue Status</p>
                    <p className="text-xs text-muted-foreground">Live tracking</p>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Your number</span>
                    <span className="font-bold text-blue-600">{queueData.userNumber}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Now serving</span>
                    <span className="font-bold text-emerald-600">{queueData.nowServing}</span>
                  </div>
                  <div className="h-2 bg-blue-100 rounded-full overflow-hidden mt-1">
                    <div className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full" style={{ width: `${queueData.waitProgress}%` }} />
                  </div>
                  <p className="text-xs text-muted-foreground text-right">{queueData.estimatedWait} remaining</p>
                </div>
              </div>

              <div className="absolute bottom-8 left-0 w-60 bg-white rounded-2xl shadow-2xl shadow-emerald-500/10 border border-border/50 p-5 animate-hero-float" style={{ animationDelay: '1.5s' }}>
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                    <CheckCircle2 className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold">Service Complete</p>
                    <p className="text-xs text-muted-foreground">Just now</p>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground">
                  Your <span className="font-medium text-foreground">{queueData.completedService}</span> request has been processed.
                </p>
              </div>

              <div className="absolute top-1/2 left-8 -translate-y-1/2 w-52 bg-white rounded-2xl shadow-2xl shadow-violet-500/10 border border-border/50 p-4 animate-hero-float" style={{ animationDelay: '3s' }}>
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="h-5 w-5 text-violet-600" />
                  <p className="text-sm font-semibold">AI Assistant</p>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  "{queueData.aiMessage}"
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll hint */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 text-muted-foreground/60 animate-bounce">
          <span className="text-xs">Scroll to explore</span>
          <ChevronDown className="h-4 w-4" />
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/*  SERVICES                                                      */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <section id="services" className="py-24 bg-gradient-to-b from-slate-50/80 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <FadeInSection className="text-center max-w-2xl mx-auto mb-16">
            <span className="inline-block px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-semibold uppercase tracking-wider mb-4">
              Our Services
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">
              Everything You Need from the Registrar
            </h2>
            <p className="mt-4 text-muted-foreground text-lg">
              From enrollment to diploma release — all registrar services are accessible through a single, streamlined platform.
            </p>
          </FadeInSection>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((svc, i) => (
              <FadeInSection key={svc.title} delay={i * 80}>
                <Card className="group relative overflow-hidden border-border/50 hover:border-blue-200 hover:shadow-xl hover:shadow-blue-500/5 transition-all duration-300 hover:-translate-y-1 h-full">
                  <CardContent className="p-6">
                    <div
                      className={`inline-flex items-center justify-center h-12 w-12 rounded-xl ${svc.bg} ${svc.iconColor} mb-4 group-hover:scale-110 transition-transform`}
                    >
                      <svc.icon className="h-6 w-6" />
                    </div>
                    <h3 className="text-lg font-semibold mb-2">{svc.title}</h3>
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      {svc.description}
                    </p>
                  </CardContent>
                  {/* Accent gradient at top */}
                  <div
                    className={`absolute top-0 inset-x-0 h-1 bg-gradient-to-r ${svc.color} opacity-0 group-hover:opacity-100 transition-opacity`}
                  />
                </Card>
              </FadeInSection>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/*  FEATURES                                                      */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <section id="features" className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <FadeInSection className="text-center max-w-2xl mx-auto mb-16">
            <span className="inline-block px-3 py-1 rounded-full bg-indigo-50 text-indigo-700 text-xs font-semibold uppercase tracking-wider mb-4">
              Platform Features
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">
              Built for the Modern Campus
            </h2>
            <p className="mt-4 text-muted-foreground text-lg">
              Serve Smart combines real-time technology with an intuitive interface so every visit to the registrar is effortless.
            </p>
          </FadeInSection>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feat, i) => (
              <FadeInSection key={feat.title} delay={i * 80}>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 h-11 w-11 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-500/20">
                    <feat.icon className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">{feat.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {feat.description}
                    </p>
                  </div>
                </div>
              </FadeInSection>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/*  HOW IT WORKS                                                  */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <section id="how-it-works" className="py-24 bg-gradient-to-b from-slate-50/80 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <FadeInSection className="text-center max-w-2xl mx-auto mb-16">
            <span className="inline-block px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold uppercase tracking-wider mb-4">
              How It Works
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">
              Four Simple Steps
            </h2>
            <p className="mt-4 text-muted-foreground text-lg">
              Go from sign-up to getting served in minutes — no paperwork, no hassle.
            </p>
          </FadeInSection>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((s, i) => (
              <FadeInSection key={s.step} delay={i * 120}>
                <div className="relative text-center">
                  {/* Step number */}
                  <div className="inline-flex items-center justify-center h-14 w-14 rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-600 text-white text-xl font-bold shadow-lg shadow-blue-500/25 mb-5">
                    {s.step}
                  </div>
                  {/* Connector line (hidden on last) */}
                  {i < steps.length - 1 && (
                    <div className="hidden lg:block absolute top-7 left-[calc(50%+40px)] w-[calc(100%-80px)] h-[2px] bg-gradient-to-r from-blue-300 to-indigo-200" />
                  )}
                  <h3 className="font-semibold text-lg mb-2">{s.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {s.description}
                  </p>
                </div>
              </FadeInSection>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/*  STUDENT TYPES                                                 */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <FadeInSection className="text-center max-w-2xl mx-auto mb-16">
            <span className="inline-block px-3 py-1 rounded-full bg-violet-50 text-violet-700 text-xs font-semibold uppercase tracking-wider mb-4">
              For Every Student
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">
              Serving All NEMSU Programs
            </h2>
            <p className="mt-4 text-muted-foreground text-lg">
              Whether you're an undergrad, a graduate student, or enrolled in the College of Law — the registrar queue system is designed for you.
            </p>
          </FadeInSection>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: BookOpen,
                title: 'Undergraduate',
                description:
                  "Bachelor's degree programs across all colleges and campuses. Enrollment, grades, and document services.",
                gradient: 'from-blue-500 to-cyan-500',
                border: 'hover:border-blue-300',
              },
              {
                icon: Award,
                title: 'Graduate School',
                description:
                  "Master's and Doctorate students can process thesis-related documents, certifications, and transcripts.",
                gradient: 'from-indigo-500 to-violet-500',
                border: 'hover:border-indigo-300',
              },
              {
                icon: Shield,
                title: 'College of Law',
                description:
                  'Juris Doctor and legal studies students — bar exam requirements, certifications, and enrollment services.',
                gradient: 'from-amber-500 to-orange-500',
                border: 'hover:border-amber-300',
              },
            ].map((prog, i) => (
              <FadeInSection key={prog.title} delay={i * 100}>
                <Card className={`h-full border-border/50 ${prog.border} hover:shadow-xl transition-all duration-300 hover:-translate-y-1 overflow-hidden`}>
                  <CardContent className="p-8 text-center">
                    <div
                      className={`inline-flex items-center justify-center h-16 w-16 rounded-2xl bg-gradient-to-br ${prog.gradient} text-white shadow-lg mb-6`}
                    >
                      <prog.icon className="h-8 w-8" />
                    </div>
                    <h3 className="text-xl font-bold mb-3">{prog.title}</h3>
                    <p className="text-muted-foreground leading-relaxed">
                      {prog.description}
                    </p>
                  </CardContent>
                </Card>
              </FadeInSection>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/*  TESTIMONIALS / SOCIAL PROOF                                   */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <section className="py-24 bg-gradient-to-br from-blue-600 via-indigo-600 to-violet-700 text-white relative overflow-hidden">
        {/* Decorative */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white/5 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 right-0 w-80 h-80 bg-white/5 rounded-full blur-3xl translate-x-1/3 translate-y-1/3" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <FadeInSection className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">
              Trusted by NEMSU Students & Staff
            </h2>
            <p className="mt-4 text-blue-100 text-lg">
              Hear what the NEMSU community has to say about the Serve Smart experience.
            </p>
          </FadeInSection>

          <div className="grid md:grid-cols-3 gap-8">
            {visibleTestimonials.map((t, i) => (
              <FadeInSection key={`${t.name}-${i}`} delay={i * 100}>
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:bg-white/15 transition-colors h-full flex flex-col">
                  <div className="flex gap-1 mb-4">
                    {[...Array(t.rating)].map((_, j) => (
                      <Star key={j} className="h-4 w-4 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <p className="text-blue-50 leading-relaxed flex-1">"{t.quote}"</p>
                  <div className="mt-5 pt-4 border-t border-white/10">
                    <p className="font-semibold">{t.name}</p>
                    <p className="text-sm text-blue-200">{t.role}</p>
                  </div>
                </div>
              </FadeInSection>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/*  FAQ                                                           */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <section id="faq" className="py-24">
        <div className="max-w-3xl mx-auto px-6">
          <FadeInSection className="text-center mb-16">
            <span className="inline-block px-3 py-1 rounded-full bg-amber-50 text-amber-700 text-xs font-semibold uppercase tracking-wider mb-4">
              FAQ
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">
              Frequently Asked Questions
            </h2>
            <p className="mt-4 text-muted-foreground text-lg">
              Got questions? We've got answers.
            </p>
          </FadeInSection>

          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <FadeInSection key={i} delay={i * 60}>
                <div className="border border-border/60 rounded-xl overflow-hidden hover:border-blue-200 transition-colors">
                  <button
                    className="w-full flex items-center justify-between px-6 py-4 text-left"
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  >
                    <span className="font-medium pr-4">{faq.q}</span>
                    <ChevronDown
                      className={`h-5 w-5 flex-shrink-0 text-muted-foreground transition-transform duration-200 ${
                        openFaq === i ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  <div
                    className={`px-6 overflow-hidden transition-all duration-300 ${
                      openFaq === i ? 'max-h-60 pb-4' : 'max-h-0'
                    }`}
                  >
                    <p className="text-muted-foreground leading-relaxed">{faq.a}</p>
                  </div>
                </div>
              </FadeInSection>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/*  CTA                                                           */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <section className="py-24 bg-gradient-to-b from-slate-50/80 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <FadeInSection>
            <div className="relative rounded-3xl bg-gradient-to-br from-blue-600 via-indigo-600 to-violet-700 p-10 sm:p-16 text-center text-white overflow-hidden">
              {/* Decorative */}
              <div className="absolute inset-0 pointer-events-none">
                <div className="absolute -top-20 -right-20 w-60 h-60 bg-white/10 rounded-full blur-2xl" />
                <div className="absolute -bottom-16 -left-16 w-48 h-48 bg-white/10 rounded-full blur-2xl" />
              </div>

              <div className="relative z-10">
                <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4">
                  Ready to Skip the Line?
                </h2>
                <p className="text-blue-100 text-lg max-w-xl mx-auto mb-8">
                  Join thousands of NEMSU students who are already using Serve Smart to save time and get things done faster at the registrar.
                </p>
                <div className="flex flex-wrap justify-center gap-4">
                  <Button
                    size="lg"
                    onClick={onGetStarted}
                    className="h-13 px-10 text-base bg-white text-blue-700 hover:bg-blue-50 shadow-xl shadow-black/10 hover:scale-[1.02] active:scale-[0.98] transition-all font-semibold"
                  >
                    Create Free Account
                    <ChevronRight className="ml-2 h-5 w-5" />
                  </Button>
                </div>
              </div>
            </div>
          </FadeInSection>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/*  FOOTER                                                        */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <footer className="border-t border-border/50 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-10">
            {/* Brand */}
            <div className="sm:col-span-2 lg:col-span-1">
              <div className="flex items-center gap-3 mb-4">
                <img src="/regiqueueai.png" alt="Logo" className="h-9 w-9 rounded-xl" />
                <div>
                  <p className="font-bold text-lg">Serve Smart</p>
                  <p className="text-xs text-muted-foreground">NEMSU Registrar Queue</p>
                </div>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Streamlining the NEMSU registrar experience with smart queue management, real-time tracking, and AI-powered assistance.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                {['Services', 'Features', 'How It Works', 'FAQ'].map((link) => (
                  <li key={link}>
                    <button
                      onClick={() => scrollTo(link.toLowerCase().replace(/\s+/g, '-'))}
                      className="hover:text-foreground transition-colors"
                    >
                      {link}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {/* Services */}
            <div>
              <h4 className="font-semibold mb-4">Services</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                {['Enrollment', 'Transcript of Records', 'Diploma Processing', 'Clearance', 'Good Moral Certificate', 'CAV'].map(
                  (svc) => (
                    <li key={svc}>{svc}</li>
                  )
                )}
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="font-semibold mb-4">Contact</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>NEMSU-Tandag Registrar's Office</li>
                <li>registrarmain@nemsu.edu.ph</li>
                <li>Mon – Fri, 8:00 AM – 5:00 PM No Noon Break</li>
                <li>Saturday, 8:00 AM – 12:00 PM | 1:00 PM - 5:00 PM</li>
              </ul>
            </div>
          </div>

          <div className="mt-12 pt-6 border-t border-border/50 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
            <p>&copy; {new Date().getFullYear()} NEMSU — Serve Smart. All rights reserved.</p>
            <div className="flex gap-6">
              <span className="hover:text-foreground transition-colors cursor-pointer">
                Privacy Policy
              </span>
              <span className="hover:text-foreground transition-colors cursor-pointer">
                Terms of Service
              </span>
            </div>
          </div>
        </div>
      </footer>

      <LandingPageChatbot onGetStarted={onGetStarted} />
    </div>
  );
}
